import { AfterViewInit, Directive, Input, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';

export interface Indentation {
  leftSide?: number;
  rightSide?: number;
}

@Directive({
  standalone: false,
  selector: '[tiTabstripNavigation]',
})
export class TabstripNavigationDirective implements AfterViewInit, OnDestroy {
  @Input('tiTabstripNavigation') tabStrip: TabStripComponent;

  @Input() arrowStyles: object = {};

  @Input() set swapLength(value: number) {
    this.translateX = value;
  }

  @Input() set indentation(indentation: Indentation) {
    this.leftIndentation = indentation.leftSide ? indentation.leftSide : this.leftIndentation;
    this.rightIndentation = indentation.rightSide ? indentation.rightSide : this.rightIndentation;
  }

  private translateX: number = 60;

  private leftIndentation: number = 0;
  private rightIndentation: number = 0;

  private readonly delta = 20;

  private list: HTMLElement;
  private listContainer: HTMLElement;

  private leftArrow: HTMLElement;
  private rightArrow: HTMLElement;

  private leftListener: Function;
  private rightListener: Function;

  private position: number = 0;

  private tabChangeSub$: Subscription;

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit() {
    this.updateArrows();
    this.renderer.setStyle(this.list, 'transition', 'transform 0.3s');

    this.tabChangeSub$ = this.tabStrip.tabSelect.subscribe((e: SelectEvent) => {
      if (e.isDefaultPrevented()) {
        return;
      }
      this.processTabOverflow(e.index);
    });
  }

  ngOnDestroy() {
    this.tabChangeSub$ && this.tabChangeSub$.unsubscribe();
  }

  private updateArrows() {
    this.listContainer = this.tabStrip.tablist.nativeElement.parentElement;
    this.list = this.tabStrip.tablist.nativeElement;
    const listWidth = this.getRealListWidth();

    if (this.listContainer.offsetWidth >= listWidth) {
      this.removeLeftArrow();
      this.removeRightArrow();
      this.position = 0;
      this.setTransform(this.position);
      return;
    }

    if (this.position >= this.leftIndentation) {
      this.removeLeftArrow();
      this.createRightArrow();
      return;
    }

    if (listWidth + this.position <= this.listContainer.offsetWidth - this.rightIndentation) {
      this.removeRightArrow();
      this.createLeftArrow();
      return;
    }

    this.createLeftArrow();
    this.createRightArrow();
  }

  private createArrow(isLeft: boolean): HTMLElement {
    const span = this.renderer.createElement('span');
    this.renderer.addClass(span, 'fa');
    if (isLeft) {
      this.renderer.addClass(span, 'fa-arrow-left');
      this.renderer.setStyle(span, 'position', 'absolute');
    } else {
      this.renderer.addClass(span, 'fa-arrow-right');
      this.renderer.setStyle(span, 'position', 'absolute');
      this.renderer.setStyle(span, 'right', '0px');
    }
    this.renderer.setStyle(span, 'color', '#386296');
    this.renderer.setStyle(span, 'cursor', 'pointer');
    Object.keys(this.arrowStyles).forEach((style) => this.renderer.setStyle(span, style, this.arrowStyles[style]));
    return span;
  }

  private createLeftArrow() {
    if (this.leftArrow) {
      return;
    }
    this.leftArrow = this.createArrow(true);
    this.renderer.insertBefore(this.listContainer, this.leftArrow, this.listContainer.children[0]);
    this.leftListener = this.renderer.listen(this.leftArrow, 'click', () => this.leftHandler());
  }

  private createRightArrow() {
    if (this.rightArrow) {
      return;
    }
    this.rightArrow = this.createArrow(false);
    this.renderer.appendChild(this.listContainer, this.rightArrow);
    this.rightListener = this.renderer.listen(this.rightArrow, 'click', () => this.rightHandler());
  }

  private leftHandler(indentation?: number) {
    indentation = indentation || this.leftIndentation;

    this.position = this.position + this.translateX >= indentation ? indentation : this.position + this.translateX;
    if (this.position === indentation) {
      this.removeLeftArrow();
    }
    !this.rightArrow && this.createRightArrow();
    this.setTransform(this.position);
  }

  private rightHandler(indentation?: number) {
    indentation = indentation || this.rightIndentation;

    const listWidth = this.getRealListWidth();
    const surplus = listWidth - this.listContainer.offsetWidth;

    this.position =
      this.position - this.translateX <= -surplus - indentation
        ? -surplus - indentation
        : this.position - this.translateX;

    if (this.position === -surplus - indentation) {
      this.removeRightArrow();
    }
    !this.leftArrow && this.createLeftArrow();
    this.setTransform(this.position);
  }

  private removeLeftArrow() {
    if (this.leftArrow) {
      this.leftListener();
      this.renderer.removeChild(this.listContainer, this.leftArrow);
      this.leftArrow = null;
    }
  }

  private removeRightArrow() {
    if (this.rightArrow) {
      this.rightListener();
      this.renderer.removeChild(this.listContainer, this.rightArrow);
      this.rightArrow = null;
    }
  }

  private processTabOverflow(index: number) {
    const tabList = <HTMLElement>this.tabStrip.tablist.nativeElement;
    const container = tabList.parentElement;
    const tab = tabList.children[index];

    const isFirstTab = index === 0;
    const isLastTab = index === tabList.children.length - 1;

    let direction: 'back' | 'forward' = null;
    let size = 0;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    if (tabRect.width >= containerRect.width) {
      // something weird, but it's possible;
      // probably need to think about this case
    } else {
      if (containerRect.left > tabRect.left + this.leftIndentation) {
        direction = 'back';
        size = containerRect.left - tabRect.left;
      }

      if (tabRect.right > containerRect.right - this.rightIndentation) {
        direction = 'forward';
        size = tabRect.right - containerRect.right;
      }
    }

    if ((direction === 'back' && !isFirstTab) || (direction === 'forward' && !isLastTab)) {
      size += 100;
    }

    if (direction) {
      direction === 'back' ? this.leftHandler(size) : this.rightHandler(size);
    }

    this.updateArrows();
  }

  private setTransform(value: number) {
    this.renderer.setStyle(this.list, 'transform', `translateX(${value}px`);
  }

  private getRealListWidth(): number {
    return Array.from(this.list.children).reduce((acc: number, item: HTMLElement) => acc + item.offsetWidth, 0);
  }
}
